import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import TimePeriodSelector from 'commons/src/components/buttons/TimePeriodSelector';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { SelectedPeriod } from 'commons/src/models/commonTypeScript';

export type GraphOptionToggle = {
    value: boolean;
    onChange: (value: boolean) => void;
};

export type Props = {
    selectGraphPeriod: (selectedPeriod: SelectedPeriod, isCustom?: boolean) => void;
    selectedPeriod: SelectedPeriod;
    onResize: (e: React.SyntheticEvent<HTMLElement>) => void;
    dateFormat: string;
    resetZoom: () => void;
    isExpanded: boolean;
    isMobile: boolean;
    loading: boolean;
    endedSegment: boolean;
    segmentStartDate?: string;
    segmentEndDate?: string;
    zoomActive: boolean;
    timeZone?: string;
    /** When provided, shows a button to reset sensor row heights to default. */
    onResetLayout?: () => void;
    /** When provided, shows a toggle button for "Show threshold lines" (synced with settings). */
    showThresholdLines?: GraphOptionToggle;
    /** When provided, shows a toggle button for "Scale graph to include threshold lines" (synced with settings). */
    scaleGraphToThresholds?: GraphOptionToggle;
    /** When provided, shows a toggle button for "Show legend on the left" (synced with settings). */
    showLegendOnLeft?: GraphOptionToggle;
};

export const OptionsRowComponent = (props: Props): React.ReactElement => {
    const {
        selectGraphPeriod,
        selectedPeriod,
        onResize,
        isExpanded,
        isMobile,
        dateFormat,
        loading,
        endedSegment,
        segmentEndDate,
        segmentStartDate,
        resetZoom,
        zoomActive,
        timeZone,
        onResetLayout,
        showThresholdLines: showThresholdLinesToggle,
        scaleGraphToThresholds: scaleGraphToThresholdsToggle,
        showLegendOnLeft: showLegendOnLeftToggle,
    } = props;
    const { t: txt } = useTranslation();
    const secondaryTogglesDisabled = showThresholdLinesToggle ? !showThresholdLinesToggle.value : false;

    const graphOptionButtonClass = 'single-device__graph-options-row__btn';

    return (
        <>
            <div
                className={classNames('single-device__options-row', 'elevation-6', {
                    'single-device__options-row--fit-height': isMobile,
                })}
            >
                {!isMobile && (
                    <div className="single-device__header-row">
                        <div className="single-device__header-row__real-time">
                            <span className="single-device__header-row__text">
                                {txt(`PeriodTypes.${selectedPeriod.name}Avg`)}
                            </span>
                        </div>
                    </div>
                )}
                <div
                    className={classNames('options-row__action-container', {
                        'options-row__action-container--full-width': isMobile,
                    })}
                >
                    <TimePeriodSelector
                        selectGraphPeriod={selectGraphPeriod}
                        selectedPeriod={selectedPeriod}
                        dateFormat={dateFormat}
                        isMobile={isMobile}
                        loading={loading}
                        endedSegment={endedSegment}
                        segmentEndDate={segmentEndDate}
                        segmentStartDate={segmentStartDate}
                        timeZone={timeZone}
                    />
                </div>
            </div>

            {!isMobile && (
                <div className="single-device__graph-options-row elevation-6">
                    <button
                        type="button"
                        className={classNames(graphOptionButtonClass, {
                            'single-device__graph-options-row__btn--active': isExpanded,
                        })}
                        onClick={onResize}
                        data-resize
                        data-testid="data-resize"
                    >
                        <span className="single-device__graph-options-row__btn-icon">
                            <MaterialIcon name="timeline" />
                        </span>
                        <span className="single-device__graph-options-row__btn-text">{txt('HistoricalData')}</span>
                    </button>
                    {showThresholdLinesToggle && (
                        <button
                            type="button"
                            className={classNames(graphOptionButtonClass, {
                                'single-device__graph-options-row__btn--active': showThresholdLinesToggle.value,
                            })}
                            onClick={(): void => showThresholdLinesToggle.onChange(!showThresholdLinesToggle.value)}
                            data-toggle-threshold-lines
                            data-testid="toggle-threshold-lines"
                        >
                            <span className="single-device__graph-options-row__btn-icon">
                                <MaterialIcon name="bar_chart" />
                            </span>
                            <span className="single-device__graph-options-row__btn-text">
                                {txt('CustomThresholds.ShowThresholdLines')}
                            </span>
                        </button>
                    )}
                    {scaleGraphToThresholdsToggle && (
                        <button
                            type="button"
                            className={classNames(graphOptionButtonClass, {
                                'single-device__graph-options-row__btn--active': scaleGraphToThresholdsToggle.value,
                            })}
                            onClick={(): void =>
                                !secondaryTogglesDisabled &&
                                scaleGraphToThresholdsToggle.onChange(!scaleGraphToThresholdsToggle.value)
                            }
                            disabled={secondaryTogglesDisabled}
                            data-toggle-scale-graph
                            data-testid="toggle-scale-graph"
                        >
                            <span className="single-device__graph-options-row__btn-icon">
                                <MaterialIcon name="aspect_ratio" />
                            </span>
                            <span className="single-device__graph-options-row__btn-text">
                                {txt('CustomThresholds.ScaleGraphToThresholds')}
                            </span>
                        </button>
                    )}
                    {showLegendOnLeftToggle && (
                        <button
                            type="button"
                            className={classNames(graphOptionButtonClass, {
                                'single-device__graph-options-row__btn--active': showLegendOnLeftToggle.value,
                            })}
                            onClick={(): void =>
                                !secondaryTogglesDisabled &&
                                showLegendOnLeftToggle.onChange(!showLegendOnLeftToggle.value)
                            }
                            disabled={secondaryTogglesDisabled}
                            data-toggle-legend
                            data-testid="toggle-legend-on-left"
                        >
                            <span className="single-device__graph-options-row__btn-icon">
                                <MaterialIcon name="label" />
                            </span>
                            <span className="single-device__graph-options-row__btn-text">
                                {txt('CustomThresholds.ShowMinMaxAndLegend')}
                            </span>
                        </button>
                    )}
                    {zoomActive && (
                        <button
                            type="button"
                            className={graphOptionButtonClass}
                            onClick={resetZoom}
                            data-reset-zoom
                            data-testid="data-resize"
                        >
                            <span className="single-device__graph-options-row__btn-icon">
                                <MaterialIcon name="zoom_out_map" />
                            </span>
                            <span className="single-device__graph-options-row__btn-text">{txt('ResetZoom')}</span>
                        </button>
                    )}
                    {onResetLayout && (
                        <button
                            type="button"
                            className={graphOptionButtonClass}
                            onClick={onResetLayout}
                            data-reset-layout
                            data-testid="data-reset-layout"
                        >
                            <span className="single-device__graph-options-row__btn-icon">
                                <MaterialIcon name="restart_alt" />
                            </span>
                            <span className="single-device__graph-options-row__btn-text">{txt('ResetLayout')}</span>
                        </button>
                    )}
                </div>
            )}
        </>
    );
};

export default OptionsRowComponent;
